import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2,
  Users,
  Target,
  CheckSquare,
  Bell,
  AlertTriangle,
  FileText,
  Sparkles,
  ArrowRight,
  Clock,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Shared";
import { useAuth } from "@/context/AuthContext";

function StatCard({ icon: Icon, label, value, testid, accent, to }) {
  const inner = (
    <div className={`crm-card p-5 transition-shadow ${to ? "hover:shadow-md cursor-pointer" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</div>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent || "bg-gray-100 text-gray-700"}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="mt-3 font-display font-bold text-3xl text-gray-900 tabular-nums">{value ?? "—"}</div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function Section({ title, icon: Icon, to, testid, children, badge }) {
  return (
    <section className="crm-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-semibold text-gray-900 flex items-center gap-2">
          {Icon ? <Icon className="w-4 h-4 text-gray-500" /> : null}
          {title}
          {badge != null ? (
            <span className="text-xs font-medium bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 tabular-nums">
              {badge}
            </span>
          ) : null}
        </h2>
        {to ? (
          <Link to={to} className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1 underline-offset-2 hover:underline">
            Alle <ArrowRight className="w-3 h-3" />
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function EmptyLine({ text }) {
  return <div className="text-sm text-gray-400 py-6 text-center">{text}</div>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: stats } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
  });
  const { data: overdueTasks = [] } = useQuery({
    queryKey: ["dashboard-overdue-tasks"],
    queryFn: async () => (await api.get("/dashboard/overdue-tasks")).data,
  });
  const { data: overdueFollowups = [] } = useQuery({
    queryKey: ["dashboard-overdue-followups"],
    queryFn: async () => (await api.get("/dashboard/overdue-followups")).data,
  });
  const { data: openQuotes = [] } = useQuery({
    queryKey: ["dashboard-open-quotes"],
    queryFn: async () => (await api.get("/dashboard/open-quotes")).data,
  });
  const { data: newLeads = [] } = useQuery({
    queryKey: ["dashboard-new-leads"],
    queryFn: async () => (await api.get("/dashboard/new-leads")).data,
  });
  const { data: needsAttention = [] } = useQuery({
    queryKey: ["dashboard-needs-attention"],
    queryFn: async () => (await api.get("/dashboard/needs-attention")).data,
  });
  const { data: activities = [] } = useQuery({
    queryKey: ["dashboard-activities"],
    queryFn: async () => (await api.get("/dashboard/recent-activities")).data,
  });

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={`Hallo, ${user?.name?.split(" ")[0] || "Team"}`}
        subtitle="Übersicht deiner offenen Punkte, Empfehlungen und letzten Aktivitäten."
        testid="dashboard-header"
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        <StatCard icon={Building2} label="Firmen" value={stats?.companies_count} testid="stat-companies" to="/companies" />
        <StatCard icon={Users} label="Kontakte" value={stats?.contacts_count} testid="stat-contacts" to="/contacts" />
        <StatCard icon={Target} label="Neue Leads" value={stats?.new_leads_count} testid="stat-new-leads" to="/leads" accent="bg-blue-50 text-blue-700" />
        <StatCard icon={AlertTriangle} label="Aufgaben überfällig" value={stats?.overdue_tasks_count} testid="stat-overdue-tasks" to="/tasks" accent={stats?.overdue_tasks_count ? "bg-rose-50 text-rose-700" : "bg-gray-100 text-gray-700"} />
        <StatCard icon={Bell} label="Wiedervorlagen überfällig" value={stats?.overdue_followups_count} testid="stat-overdue-followups" to="/followups" accent={stats?.overdue_followups_count ? "bg-rose-50 text-rose-700" : "bg-gray-100 text-gray-700"} />
        <StatCard icon={FileText} label="Offene Angebote" value={stats?.open_quotes_count} testid="stat-open-quotes" to="/quotes" accent="bg-violet-50 text-violet-700" />
      </div>

      {/* Kontaktpflege empfohlen */}
      <div className="mt-6">
        <Section
          title="Kontaktpflege empfohlen"
          icon={Sparkles}
          testid="needs-attention-card"
          badge={needsAttention.length}
        >
          {needsAttention.length === 0 ? (
            <EmptyLine text="Alle aktiven Firmen wurden in den letzten 30 Tagen kontaktiert." />
          ) : (
            <ul className="divide-y divide-gray-100">
              {needsAttention.slice(0, 8).map((item) => (
                <li
                  key={item.company_id}
                  className="py-2.5 flex items-center justify-between gap-3 hover:bg-gray-50 px-2 -mx-2 rounded cursor-pointer"
                  onClick={() => navigate(`/companies/${item.company_id}`)}
                  data-testid={`needs-attention-item-${item.company_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">{item.company_name}</div>
                    <div className="text-xs text-gray-500">
                      {item.days_since_contact === null || item.days_since_contact === undefined
                        ? "Noch nie kontaktiert"
                        : `Letzter Kontakt vor ${item.days_since_contact} Tagen`}
                      {item.status ? <span className="ml-2 text-gray-400">· {item.status}</span> : null}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 shrink-0" />
                </li>
              ))}
            </ul>
          )}
          {needsAttention.length > 8 ? (
            <div className="text-xs text-gray-500 mt-3 text-center">+{needsAttention.length - 8} weitere Firmen</div>
          ) : null}
        </Section>
      </div>

      {/* Lists row 1 */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Überfällige Aufgaben" icon={AlertTriangle} to="/tasks" testid="dashboard-overdue-tasks-card" badge={overdueTasks.length}>
          {overdueTasks.length === 0 ? (
            <EmptyLine text="Keine überfälligen Aufgaben – sauber!" />
          ) : (
            <ul className="space-y-1.5">
              {overdueTasks.slice(0, 6).map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-3 py-2 border-b last:border-b-0 border-gray-100" data-testid={`overdue-task-${t.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{t.title}</div>
                    <div className="text-xs text-rose-600">
                      fällig {new Date(t.due_date).toLocaleDateString("de-DE")}
                    </div>
                  </div>
                  <StatusBadge value={t.priority} />
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Überfällige Wiedervorlagen" icon={Bell} to="/followups" testid="dashboard-overdue-followups-card" badge={overdueFollowups.length}>
          {overdueFollowups.length === 0 ? (
            <EmptyLine text="Keine überfälligen Wiedervorlagen – sauber!" />
          ) : (
            <ul className="space-y-1.5">
              {overdueFollowups.slice(0, 6).map((f) => (
                <li key={f.id} className="flex items-center justify-between gap-3 py-2 border-b last:border-b-0 border-gray-100" data-testid={`overdue-followup-${f.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{f.title}</div>
                    <div className="text-xs text-rose-600">
                      fällig {new Date(f.due_date).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {/* Lists row 2 */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Section title="Offene Angebote" icon={FileText} to="/quotes" testid="dashboard-open-quotes-card" badge={openQuotes.length}>
          {openQuotes.length === 0 ? (
            <EmptyLine text="Aktuell keine offenen Angebote." />
          ) : (
            <ul className="space-y-1.5">
              {openQuotes.slice(0, 6).map((q) => (
                <li key={q.id} className="flex items-center justify-between gap-3 py-2 border-b last:border-b-0 border-gray-100" data-testid={`open-quote-${q.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      <span className="font-mono text-xs text-gray-500 mr-2">{q.quote_number}</span>
                      {q.title}
                    </div>
                    <div className="text-xs text-gray-500">{q.status}</div>
                  </div>
                  {q.amount ? (
                    <div className="text-sm font-medium tabular-nums text-gray-900">
                      {new Intl.NumberFormat("de-DE", { style: "currency", currency: q.currency || "EUR" }).format(Number(q.amount))}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Neue Leads" icon={Target} to="/leads" testid="dashboard-new-leads-card" badge={newLeads.length}>
          {newLeads.length === 0 ? (
            <EmptyLine text="Aktuell keine neuen Leads." />
          ) : (
            <ul className="space-y-1.5">
              {newLeads.slice(0, 6).map((l) => (
                <li key={l.id} className="flex items-center justify-between gap-3 py-2 border-b last:border-b-0 border-gray-100" data-testid={`new-lead-${l.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{l.title}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {l.company_name || l.contact_name || "—"}
                    </div>
                  </div>
                  <StatusBadge value={l.status} />
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Letzte Aktivitäten" icon={Clock} to="/activities" testid="dashboard-activities-card" badge={activities.length}>
          {activities.length === 0 ? (
            <EmptyLine text="Noch keine Aktivitäten erfasst." />
          ) : (
            <ul className="space-y-1.5">
              {activities.slice(0, 6).map((a) => (
                <li key={a.id} className="py-2 border-b last:border-b-0 border-gray-100" data-testid={`recent-activity-${a.id}`}>
                  <div className="text-sm font-medium text-gray-900 truncate">{a.subject}</div>
                  <div className="text-xs text-gray-500">
                    {new Date(a.occurred_at).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })} · {a.type}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}
